import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';

/**
 * DNA Family SPECIAL (docs/globalhealth/DNA_FAMILY_SPEZIAL_MACHBARKEIT.md): a
 * buyer of the regular DNA & Health SPECIAL may invite up to ten family members,
 * each of whom buys their own DNA Family SPECIAL ($999 one-time, no voluntary
 * money-back guarantee) on a separate account.
 *
 * Member API  /api/funnel/family/*            (Api::Funnel::FamilyController)
 * Public API  /api/funnel/family/invitations/:token  (status of one link)
 * Admin API   /api/admin/health/family_invitations
 *
 * Privacy (§4.6): the main account only ever sees its own invitation rows —
 * label, the e-mail IT entered, status and dates — never the family member's
 * name, health or kit data.
 */

export type FamilyInvitationStatus =
  'pending' | 'accepted' | 'purchased' | 'expired' | 'revoked' | 'released';

export interface FamilyInvitation {
  id:            number;
  label?:        string | null;
  email?:        string | null;
  status:        FamilyInvitationStatus;
  /** Present for pending/accepted only — a used or dead link is never handed out. */
  link?:         string | null;
  created_at:    string;
  expires_at?:   string | null;
  accepted_at?:  string | null;
  purchased_at?: string | null;
  revocable:     boolean;
}

/** The slot counters (also in `user_payload.family`). used = open + purchased. */
export interface FamilyCounters {
  enabled:   boolean;
  qualified: boolean;
  slots:     number;
  used:      number;
  open:      number;
  purchased: number;
  remaining: number;
}

export interface FamilyOverview extends FamilyCounters {
  price:       number | string;
  product_key: 'dna_family_special';
  invitations: FamilyInvitation[];
}

export type FamilyTokenStatus = 'valid' | 'expired' | 'revoked' | 'used' | 'invalid';

/** GET /funnel/family/invitations/:token — public, reveals no more than this. */
export interface FamilyTokenInfo {
  status:              FamilyTokenStatus;
  inviter_first_name?: string | null;
  price?:              number | string;
  product_key?:        'dna_family_special';
  plan?:               'dna_family_special';
  /** The signed-in viewer already accepted this invitation. */
  accepted_by_you?:    boolean;
}

export interface FamilyAcceptResponse {
  status: 'accepted';
  /** Wizard URL without the language prefix (`/membership/apply?…`). */
  next:   string;
}

/** Error codes of accept with their own message (`family_join.errors.<code>`). */
export const FAMILY_ACCEPT_ERRORS = [
  'self_invitation', 'already_bundle_owner', 'practitioner_active', 'invitation_expired',
  'invitation_revoked', 'invitation_used', 'invitation_invalid', 'family_quota_exhausted',
  // The account already holds a live accepted invitation from ANOTHER main account.
  'invitation_already_accepted'
];

// ----- Admin -----------------------------------------------------------------

export interface FamilyAdminPerson {
  id:    number;
  name?: string | null;
  email?: string | null;
}

export interface FamilyAdminInvitation {
  id:             number;
  main_user:      FamilyAdminPerson;
  invitee_user?:  FamilyAdminPerson | null;
  label?:         string | null;
  email?:         string | null;
  status:         FamilyInvitationStatus;
  order_id?:      number | null;
  created_at:     string;
  expires_at?:    string | null;
  accepted_at?:   string | null;
  purchased_at?:  string | null;
  released_at?:   string | null;
  release_reason?: string | null;
}

export interface FamilyAdminListResponse {
  invitations: FamilyAdminInvitation[];
  total:       number;
  page:        number;
  per_page:    number;
  statuses:    string[];
}

@Injectable({ providedIn: 'root' })
export class FamilyService {
  private http = inject(HttpClient);

  // ----- Main account -----------------------------------------------------------

  /** 404 not_available (switch off), 403 family_not_qualified. */
  overview(): Observable<FamilyOverview> {
    return this.http.get<FamilyOverview>('/funnel/family');
  }

  /** 201; 409 family_quota_exhausted; 422 email_invalid | label_too_long. */
  createInvitation(input: { label?: string | null; email?: string | null; language: string }): Observable<{ invitation: FamilyInvitation }> {
    const body: Record<string, string> = { language: input.language };
    if (input.label?.trim()) body['label'] = input.label.trim();
    if (input.email?.trim()) body['email'] = input.email.trim();
    return this.http.post<{ invitation: FamilyInvitation }>('/funnel/family/invitations', body);
  }

  /** 409 not_revocable. */
  revoke(id: number): Observable<{ invitation: FamilyInvitation }> {
    return this.http.post<{ invitation: FamilyInvitation }>(`/funnel/family/invitations/${id}/revoke`, {});
  }

  /** 422 no_email. */
  resend(id: number): Observable<{ status: 'sent' }> {
    return this.http.post<{ status: 'sent' }>(`/funnel/family/invitations/${id}/resend`, {});
  }

  // ----- Invitee ------------------------------------------------------------------

  /** Public status of a link. */
  tokenStatus(token: string): Observable<FamilyTokenInfo> {
    return this.http.get<FamilyTokenInfo>(`/funnel/family/invitations/${encodeURIComponent(token)}`);
  }

  /** Signed in: binds the invitation to this account. 422 {code} — see FAMILY_ACCEPT_ERRORS. */
  accept(token: string): Observable<FamilyAcceptResponse> {
    return this.http.post<FamilyAcceptResponse>(`/funnel/family/invitations/${encodeURIComponent(token)}/accept`, {});
  }

  // ----- Admin --------------------------------------------------------------------

  adminList(filters: { page?: number; per_page?: number; status?: string; search?: string }): Observable<FamilyAdminListResponse> {
    const params: Record<string, string> = {};
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') params[key] = String(value);
    });
    return this.http.get<FamilyAdminListResponse>('/admin/health/family_invitations', { params });
  }

  /** Frees the slot of a purchased invitation. 422 reason_required, 409 not_releasable. */
  adminRelease(id: number, reason: string): Observable<{ invitation: FamilyAdminInvitation }> {
    return this.http.post<{ invitation: FamilyAdminInvitation }>(`/admin/health/family_invitations/${id}/release`, { reason });
  }
}

/** Copies a text (Clipboard API, textarea fallback — the dashboard's copyReferral pattern). */
export function copyText(text: string): Promise<boolean> {
  const fallback = (): boolean => {
    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.style.position = 'fixed';
    textarea.style.opacity = '0';
    document.body.appendChild(textarea);
    try {
      textarea.select();
      return document.execCommand('copy');
    } catch {
      return false;
    } finally {
      document.body.removeChild(textarea);
    }
  };
  if (navigator.clipboard?.writeText) {
    return navigator.clipboard.writeText(text).then(() => true).catch(() => fallback());
  }
  return Promise.resolve(fallback());
}
